import { GameEngine } from '../engine/GameEngine';
import { FeatureConfig } from '../types/game.types';

interface SavedSettings {
    rtp: number;
    freeSpinFreq: number;
    wildFreq: number;
    minBet: number;
    maxBet: number;
    paytable?: any;
    reelStrips?: any;
}

export class AdminPanel {
    private panel: HTMLElement | null = null;
    private toggleButton: HTMLElement | null = null;
    private isOpen: boolean = false;
    private gameEngine: GameEngine | null = null;
    
    init(): void {
        this.panel = document.getElementById('admin-panel');
        this.toggleButton = document.getElementById('admin-toggle');
        
        if (!this.panel || !this.toggleButton) {
            console.error('Admin panel elements not found');
            return;
        }
        
        this.setupEventListeners();
        this.loadSettings();
        
        // Try to connect to game engine when available
        const checkGameEngine = () => {
            if ((window as any).gameEngine) {
                this.gameEngine = (window as any).gameEngine;
            } else {
                setTimeout(checkGameEngine, 100);
            }
        };
        checkGameEngine();
    }
    
    private setupEventListeners(): void {
        this.toggleButton?.addEventListener('click', () => {
            this.toggle();
        });
        
        document.getElementById('rtp-target')?.addEventListener('change', (e) => {
            const target = e.target as HTMLInputElement;
            this.updateRTP(parseFloat(target.value));
        });
        
        document.getElementById('freespin-freq')?.addEventListener('change', (e) => {
            const target = e.target as HTMLInputElement;
            this.updateFreeSpinFrequency(parseFloat(target.value) / 100);
        });
        
        document.getElementById('wild-freq')?.addEventListener('change', (e) => {
            const target = e.target as HTMLInputElement;
            this.updateWildFrequency(parseFloat(target.value) / 100);
        });
        
        document.getElementById('min-bet')?.addEventListener('change', (e) => {
            const target = e.target as HTMLInputElement;
            this.updateMinBet(parseInt(target.value));
        });
        
        document.getElementById('max-bet')?.addEventListener('change', (e) => {
            const target = e.target as HTMLInputElement;
            this.updateMaxBet(parseInt(target.value));
        });
        
        document.getElementById('save-settings')?.addEventListener('click', () => {
            this.saveSettings();
        });
        
        document.getElementById('load-settings')?.addEventListener('click', () => {
            this.loadSettings();
        });
        
        document.getElementById('export-json')?.addEventListener('click', () => {
            this.exportJSON();
        });
        
        document.getElementById('import-json')?.addEventListener('change', (e) => {
            const target = e.target as HTMLInputElement;
            if (target.files && target.files[0]) {
                this.importJSON(target.files[0]);
            }
        });
    }
    
    private toggle(): void {
        this.isOpen = !this.isOpen;
        if (this.panel) {
            if (this.isOpen) {
                this.panel.classList.add('active');
            } else {
                this.panel.classList.remove('active');
            }
        }
    }
    
    setGameEngine(engine: GameEngine): void {
        this.gameEngine = engine;
    }
    
    private updateRTP(value: number): void {
        if (this.gameEngine) {
            this.gameEngine.updateConfig({ targetRTP: value / 100 });
        }
        this.saveToLocalStorage();
    }
    
    private updateFreeSpinFrequency(value: number): void {
        if (this.gameEngine) {
            const features: Partial<FeatureConfig> = {
                freeSpins: {
                    triggerCount: 3,
                    spinsAwarded: 10,
                    multiplier: 3,
                    retriggerAllowed: true,
                    triggerChance: value
                }
            };
            this.gameEngine.updateFeatures(features);
        }
        this.saveToLocalStorage();
    }
    
    private updateWildFrequency(value: number): void {
        if (this.gameEngine) {
            const features: Partial<FeatureConfig> = {
                randomWild: {
                    chance: value,
                    minWilds: 2,
                    maxWilds: 5
                }
            };
            this.gameEngine.updateFeatures(features);
        }
        this.saveToLocalStorage();
    }
    
    private updateMinBet(value: number): void {
        if (this.gameEngine) {
            this.gameEngine.updateConfig({ minBet: value });
        }
        this.saveToLocalStorage();
    }
    
    private updateMaxBet(value: number): void {
        if (this.gameEngine) {
            this.gameEngine.updateConfig({ maxBet: value });
        }
        this.saveToLocalStorage();
    }
    
    private saveSettings(): void {
        const settings = this.getCurrentSettings();
        localStorage.setItem('slotGameSettings', JSON.stringify(settings));
        this.showNotification('Settings saved!');
    }
    
    private loadSettings(): void {
        const saved = localStorage.getItem('slotGameSettings');
        if (saved) {
            try {
                const settings: SavedSettings = JSON.parse(saved);
                this.applySettings(settings);
                this.showNotification('Settings loaded!');
            } catch (error) {
                console.error('Failed to load settings:', error);
                this.showNotification('Failed to load settings!', true);
            }
        }
    }
    
    private getCurrentSettings(): SavedSettings {
        const rtpInput = document.getElementById('rtp-target') as HTMLInputElement;
        const freespinInput = document.getElementById('freespin-freq') as HTMLInputElement;
        const wildInput = document.getElementById('wild-freq') as HTMLInputElement;
        const minBetInput = document.getElementById('min-bet') as HTMLInputElement;
        const maxBetInput = document.getElementById('max-bet') as HTMLInputElement;
        
        return {
            rtp: parseFloat(rtpInput?.value || '96'),
            freeSpinFreq: parseFloat(freespinInput?.value || '1'),
            wildFreq: parseFloat(wildInput?.value || '10'),
            minBet: parseInt(minBetInput?.value || '1'),
            maxBet: parseInt(maxBetInput?.value || '100')
        };
    }
    
    private applySettings(settings: SavedSettings): void {
        const rtpInput = document.getElementById('rtp-target') as HTMLInputElement;
        const freespinInput = document.getElementById('freespin-freq') as HTMLInputElement;
        const wildInput = document.getElementById('wild-freq') as HTMLInputElement;
        const minBetInput = document.getElementById('min-bet') as HTMLInputElement;
        const maxBetInput = document.getElementById('max-bet') as HTMLInputElement;
        
        if (rtpInput) rtpInput.value = settings.rtp.toString();
        if (freespinInput) freespinInput.value = settings.freeSpinFreq.toString();
        if (wildInput) wildInput.value = settings.wildFreq.toString();
        if (minBetInput) minBetInput.value = settings.minBet.toString();
        if (maxBetInput) maxBetInput.value = settings.maxBet.toString();
        
        this.updateRTP(settings.rtp);
        this.updateFreeSpinFrequency(settings.freeSpinFreq / 100);
        this.updateWildFrequency(settings.wildFreq / 100);
        this.updateMinBet(settings.minBet);
        this.updateMaxBet(settings.maxBet);
    }
    
    private exportJSON(): void {
        const settings = this.getCurrentSettings();
        const dataStr = JSON.stringify(settings, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        
        const exportLink = document.createElement('a');
        exportLink.setAttribute('href', dataUri);
        exportLink.setAttribute('download', 'slot-settings.json');
        document.body.appendChild(exportLink);
        exportLink.click();
        document.body.removeChild(exportLink);
        
        this.showNotification('Settings exported!');
    }
    
    private async importJSON(file: File): Promise<void> {
        try {
            const text = await file.text();
            const settings: SavedSettings = JSON.parse(text);
            this.applySettings(settings);
            this.showNotification('Settings imported!');
        } catch (error) {
            console.error('Failed to import settings:', error);
            this.showNotification('Failed to import settings!', true);
        }
    }
    
    private saveToLocalStorage(): void {
        const settings = this.getCurrentSettings();
        localStorage.setItem('slotGameTempSettings', JSON.stringify(settings));
    }
    
    private showNotification(message: string, isError: boolean = false): void {
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            padding: 15px 30px;
            background: ${isError ? 'linear-gradient(45deg, #FF0000, #FF6666)' : 'linear-gradient(45deg, #00FF00, #00FFFF)'};
            color: white;
            border-radius: 25px;
            font-family: 'Bungee', cursive;
            font-size: 16px;
            z-index: 10000;
            animation: slideDown 0.3s ease;
        `;
        notification.textContent = message;
        
        document.body.appendChild(notification);
        
        setTimeout(() => {
            notification.style.animation = 'slideUp 0.3s ease';
            setTimeout(() => {
                document.body.removeChild(notification);
            }, 300);
        }, 2000);
    }
}